import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import cx from 'classnames';
import moment from 'moment';

import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Accordion from 'react-bootstrap/Accordion';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

import Loader from '../../Common/Loader';
import getDriverDocuments from './getDriverDocuments.graphql';
import messages from '../../../locale/messages';

import s from './DocumentViewer.css';
import bt from '../../commonStyle.css';

class DocumentViewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showUploadModal: false,
      selectedDocumentType: '',
      uploading: false,
      uploadProgress: 0,
      restDocuments: null,
      loadingRestData: false,
      hasLoadedDocuments: false,
      rawDataCache: {}, // Cache for loaded raw data
      loadingRawData: {} // Track which documents are loading raw data
    };
  }

  async componentDidMount() {
    // Only fetch REST data once when component mounts
    if (!this.state.hasLoadedDocuments) {
      await this.fetchRestDocuments();
    }
  }

  async fetchRestDocuments() {
    // Prevent multiple simultaneous calls
    if (this.state.loadingRestData || this.state.hasLoadedDocuments) {
      return;
    }

    this.setState({ loadingRestData: true });

    try {
      const { driverId } = this.props;
      
      // Use the proper admin endpoint to get ALL documents for this specific driver
      const response = await fetch(`http://localhost:4000/api/admin/user-documents/${driverId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 200 && data.data) {
          // Only setState if component is still mounted
          if (this.state) {
            this.setState({ 
              restDocuments: data.data,
              loadingRestData: false,
              hasLoadedDocuments: true
            });
          }
          return;
        }
      }
      
      // If endpoint fails, show empty state
      if (this.state) {
        this.setState({ 
          restDocuments: [],
          loadingRestData: false,
          hasLoadedDocuments: true
        });
      }
      
    } catch (error) {
      console.error('Error fetching user documents:', error);
      // Only setState if component is still mounted
      if (this.state) {
        this.setState({ 
          restDocuments: [],
          loadingRestData: false, 
          hasLoadedDocuments: true 
        });
      }
    }
  }

  handleImageClick = (document) => {
    // Check if we have S3 URL from REST data
    const restDoc = this.state.restDocuments?.find(d => d.id === document.id);
    
    if (restDoc && restDoc.s3Url) {
      // Open S3 URL directly
      window.open(restDoc.s3Url, '_blank');
    } else {
      // Document not available in S3
      alert('Document not yet uploaded to cloud storage');
    }
  }

  loadRawData = async (documentId) => {
    // Check if already loading or loaded
    if (this.state.loadingRawData[documentId] || this.state.rawDataCache[documentId]) {
      return;
    }

    // Set loading state
    this.setState(prevState => ({
      loadingRawData: {
        ...prevState.loadingRawData,
        [documentId]: true
      }
    }));

    try {
      const response = await fetch(`http://localhost:4000/api/admin/document/${documentId}/raw-data`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 200 && data.data.rawTextractData) {
          // Cache the raw data
          this.setState(prevState => ({
            rawDataCache: {
              ...prevState.rawDataCache,
              [documentId]: data.data.rawTextractData
            },
            loadingRawData: {
              ...prevState.loadingRawData,
              [documentId]: false
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading raw data:', error);
      // Clear loading state on error
      this.setState(prevState => ({
        loadingRawData: {
          ...prevState.loadingRawData,
          [documentId]: false
        }
      }));
    }
  }

  // Modal removed - documents now open in new tab

  openUploadModal = (documentType) => {
    this.setState({
      showUploadModal: true,
      selectedDocumentType: documentType
    });
  }

  closeUploadModal = () => {
    this.setState({
      showUploadModal: false,
      selectedDocumentType: '',
      uploading: false,
      uploadProgress: 0
    });
  }

  handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const { driverId } = this.props;
    const { selectedDocumentType } = this.state;

    this.setState({ uploading: true, uploadProgress: 0 });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', this.mapDocumentType(selectedDocumentType));
      formData.append('userId', driverId);

      const response = await fetch('/uploadLicenceV2', {
        method: 'POST',
        body: formData,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.setState({ uploadProgress: progress });
        }
      });

      if (response.ok) {
        alert('Document uploaded successfully!');
        this.closeUploadModal();
        // Refresh the page to show new document
        window.location.reload();
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      this.setState({ uploading: false, uploadProgress: 0 });
    }
  }

  mapDocumentType = (docType) => {
    const typeMap = {
      'license_front': 'front',
      'license_back': 'back', 
      'registration': 'vehicleRC',
      'insurance': 'vehicleInsurance',
      'lasdri_card': 'lasdri_card',
      'vehicle_inspection': 'vehicle_inspection',
      'roadworthiness': 'roadworthiness',
      'hackney_permit': 'hackney_permit'
    };
    return typeMap[docType] || docType;
  }

  getStatusBadge = (status) => {
    const statusMap = {
      'completed': { variant: 'success', text: 'Completed' },
      'processing': { variant: 'warning', text: 'Processing' },
      'pending': { variant: 'secondary', text: 'Pending' },
      'failed': { variant: 'danger', text: 'Failed' },
      'low_quality': { variant: 'warning', text: 'Low Quality' }
    };
    
    const statusInfo = statusMap[status] || { variant: 'secondary', text: status };
    return (
      <Badge variant={statusInfo.variant} className={s.statusBadge}>
        {statusInfo.text}
      </Badge>
    );
  }

  getDocumentTypeTitle = (documentType) => {
    const typeMap = {
      'license_front': 'Driver\'s License (Front)',
      'license_back': 'Driver\'s License (Back)',
      'lasdri_card': 'LASDRI Card',
      'vehicle_inspection': 'Vehicle Inspection Report',
      'insurance': 'Vehicle Insurance',
      'roadworthiness': 'Roadworthiness Certificate',
      'hackney_permit': 'Hackney Permit',
      'registration': 'Vehicle Registration'
    };
    
    return typeMap[documentType] || documentType;
  }

  renderDisplayFields = (displayFields) => {
    if (!displayFields) return null;
    
    try {
      const fields = typeof displayFields === 'string' ? JSON.parse(displayFields) : displayFields;
      
      return (
        <div className={s.displayFields}>
          <h6 className={s.displayFieldsTitle}>Key Information</h6>
          <div className={s.displayFieldsGrid}>
            {fields.issueDate && (
              <div className={s.displayField}>
                <strong>Issue Date:</strong>
                <span className={s.dateValue}>{this.formatDate(fields.issueDate)}</span>
              </div>
            )}
            {fields.expiryDate && (
              <div className={s.displayField}>
                <strong>Expiry Date:</strong>
                <span className={this.isExpired(fields.expiryDate) ? s.expiredDate : s.dateValue}>
                  {this.formatDate(fields.expiryDate)}
                  {this.isExpired(fields.expiryDate) && <span className={s.expiredLabel}> (EXPIRED)</span>}
                </span>
              </div>
            )}
            {fields.dateOfBirth && (
              <div className={s.displayField}>
                <strong>Date of Birth:</strong>
                <span className={s.dateValue}>{this.formatDate(fields.dateOfBirth)}</span>
              </div>
            )}
            {fields.licenseNumber && (
              <div className={s.displayField}>
                <strong>License Number:</strong>
                <span className={s.idValue}>{fields.licenseNumber}</span>
              </div>
            )}
            {fields.lasdriCardId && (
              <div className={s.displayField}>
                <strong>LASDRI Card ID:</strong>
                <span className={s.idValue}>{fields.lasdriCardId}</span>
              </div>
            )}
            {fields.policyNumber && (
              <div className={s.displayField}>
                <strong>Policy Number:</strong>
                <span className={s.idValue}>{fields.policyNumber}</span>
              </div>
            )}
            {fields.documentNumber && (
              <div className={s.displayField}>
                <strong>Document Number:</strong>
                <span className={s.idValue}>{fields.documentNumber}</span>
              </div>
            )}
            {fields.documentType && (
              <div className={s.displayField}>
                <strong>Document Type:</strong>
                <span className={s.typeValue}>{fields.documentType}</span>
              </div>
            )}
          </div>
          {fields.extractedAt && (
            <div className={s.extractedTimestamp}>
              <small>Extracted: {moment(fields.extractedAt).format('MMM DD, YYYY HH:mm')}</small>
            </div>
          )}
        </div>
      );
    } catch (e) {
      return <p className={s.error}>Error parsing display fields</p>;
    }
  }

  formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return moment(dateStr).format('MMM DD, YYYY');
    } catch (e) {
      return dateStr;
    }
  }

  isExpired = (dateStr) => {
    if (!dateStr) return false;
    try {
      return moment(dateStr).isBefore(moment(), 'day');
    } catch (e) {
      return false;
    }
  }

  renderExtractedFields = (extractedFields) => {
    if (!extractedFields) return <p className={s.noData}>No extracted data available</p>;
    
    try {
      const fields = typeof extractedFields === 'string' ? JSON.parse(extractedFields) : extractedFields;
      
      return (
        <div className={s.extractedFields}>
          <h6 className={s.allFieldsTitle}>All Extracted Fields</h6>
          {Object.entries(fields).map(([key, value]) => {
            // Handle both simple values and complex Textract objects
            let displayValue = 'N/A';
            if (value && typeof value === 'object' && value.value !== undefined) {
              displayValue = value.value || 'N/A';
            } else if (value !== null && value !== undefined) {
              displayValue = value.toString();
            }
            
            return (
              <div key={key} className={s.fieldRow}>
                <strong className={s.fieldLabel}>{this.formatFieldName(key)}:</strong>
                <span className={s.fieldValue}>{displayValue}</span>
              </div>
            );
          })}
        </div>
      );
    } catch (e) {
      return <p className={s.error}>Error parsing extracted fields</p>;
    }
  }

  formatFieldName = (fieldName) => {
    // Convert camelCase and snake_case to readable format
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }

  renderRawTextractData = (documentId) => {
    const { rawDataCache, loadingRawData } = this.state;
    const rawData = rawDataCache[documentId];
    const isLoading = loadingRawData[documentId];

    if (isLoading) {
      return (
        <div className={s.loadingRaw}>
          <p>Loading raw Textract data...</p>
          <div className={s.spinner}></div>
        </div>
      );
    }

    if (!rawData) {
      return (
        <div className={s.rawDataActions}>
          <Button 
            onClick={() => this.loadRawData(documentId)}
            style={{
              backgroundColor: 'var(--btn-primary-bg)',
              borderColor: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-color)',
              fontWeight: '500'
            }}
          >
            Load Raw Textract Data
          </Button>
          <p className={s.rawDataHint}>
            Click to load detailed Textract analysis data for this document
          </p>
        </div>
      );
    }
    
    try {
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      return (
        <pre className={s.rawData}>
          {JSON.stringify(data, null, 2)}
        </pre>
      );
    } catch (e) {
      return <p className={s.error}>Error parsing raw data</p>;
    }
  }

  getAvailableDocumentTypes = () => {
    return [
      { value: 'license_front', label: 'Driver License (Front)', required: true },
      { value: 'license_back', label: 'Driver License (Back)', required: true },
      { value: 'lasdri_card', label: 'LASDRI Card', required: false },
      { value: 'registration', label: 'Vehicle Registration', required: true },
      { value: 'insurance', label: 'Vehicle Insurance', required: true },
      { value: 'vehicle_inspection', label: 'Vehicle Inspection Report', required: true },
      { value: 'vehicle_plate_photo', label: 'Vehicle + License Plate Photo', required: true },
      { value: 'roadworthiness', label: 'Roadworthiness Certificate', required: false },
      { value: 'hackney_permit', label: 'Hackney Permit', required: false }
    ];
  }
  
  isDocumentRequired = (docType) => {
    const docTypes = this.getAvailableDocumentTypes();
    const found = docTypes.find(dt => dt.value === docType);
    return found ? found.required : true;
  }

  getDocumentTypeLabel = (docType) => {
    const docTypes = this.getAvailableDocumentTypes();
    const found = docTypes.find(dt => dt.value === docType);
    return found ? found.label : docType;
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { driverDocuments, driverId } = this.props;

    // Debug logging to catch infinite renders
    console.log('DocumentViewer render called', {
      graphqlLoading: driverDocuments.loading,
      restLoading: this.state.loadingRestData,
      hasLoaded: this.state.hasLoadedDocuments,
      docsCount: this.state.restDocuments?.length || 0
    });

    if (driverDocuments.loading || this.state.loadingRestData) {
      return <Loader type="circle" />;
    }

    const driver = driverDocuments.getDriver;
    const documents = this.state.restDocuments || [];

    return (
      <div className={cx(s.container, bt.space5)}>
        <div className={cx(s.header, bt.space3)}>
          <h3>Document Management</h3>
          {driver && (
            <p className={s.driverInfo}>
              Driver: <strong>{driver.profile?.firstName} {driver.profile?.lastName}</strong> 
              {' '}({driver.email})
            </p>
          )}
        </div>

        <Row>
          <Col md={12}>
            <Card>
              <Card.Header>
                <h5>Uploaded Documents ({documents.length})</h5>
              </Card.Header>
              <Card.Body>
                {documents.length === 0 ? (
                  <p className={s.noDocuments}>No documents uploaded yet.</p>
                ) : (
                  <Accordion>
                    {documents.map((doc, index) => (
                      <Card key={doc.id}>
                        <Accordion.Toggle as={Card.Header} eventKey={index.toString()} className={s.documentHeader}>
                          <div className={s.documentTitle}>
                            <span className={s.docType}>
                              {this.getDocumentTypeTitle(doc.documentType)}
                            </span>
                            <Badge 
                              variant={this.isDocumentRequired(doc.documentType) ? "danger" : "secondary"}
                              className={s.requirementBadge}
                            >
                              {this.isDocumentRequired(doc.documentType) ? 'REQUIRED' : 'OPTIONAL'}
                            </Badge>
                            {this.getStatusBadge(doc.status)}
                            {doc.confidence && (
                              <Badge 
                                variant={doc.confidence < 85 ? "danger" : "info"} 
                                className={s.confidenceBadge}
                              >
                                {Math.round(doc.confidence)}% confidence
                                {doc.confidence < 85 && " (Below threshold)"}
                              </Badge>
                            )}
                          </div>
                          <small className={s.uploadDate}>
                            Uploaded: {moment(doc.createdAt).format('MMM DD, YYYY HH:mm')}
                          </small>
                        </Accordion.Toggle>
                        
                        <Accordion.Collapse eventKey={index.toString()}>
                          <Card.Body>
                            <Row>
                              <Col md={4}>
                                <div className={s.documentSection}>
                                  <h6>Document</h6>
                                  {doc.id ? (
                                    <Button
                                      onClick={() => this.handleImageClick(doc)}
                                      className="w-100"
                                      style={{
                                        backgroundColor: 'var(--btn-primary-bg)',
                                        borderColor: 'var(--btn-primary-bg)',
                                        color: 'var(--btn-primary-color)',
                                        fontWeight: '500'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = 'var(--btn-primary-bg-hover)';
                                        e.target.style.borderColor = 'var(--btn-primary-bg-hover)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'var(--btn-primary-bg)';
                                        e.target.style.borderColor = 'var(--btn-primary-bg)';
                                      }}
                                    >
                                      <i className="fa fa-external-link"></i> Open Document
                                    </Button>
                                  ) : (
                                    <p className={s.noDocument}>No document available</p>
                                  )}
                                </div>
                              </Col>
                              
                              <Col md={8}>
                                <div className={s.dataSection}>
                                  {/* Display Fields - Key Information First */}
                                  {this.renderDisplayFields(doc.displayFields)}
                                  
                                  {/* Extracted Fields - All Technical Data */}
                                  <div className={s.technicalData}>
                                    {this.renderExtractedFields(doc.extractedFields)}
                                  </div>
                                  
                                  {doc.errorMessage && (
                                    <div className={s.errorSection}>
                                      <h6 className={s.errorTitle}>
                                        {doc.status === 'low_quality' ? 'Quality Issue' : 'Error Message'}
                                      </h6>
                                      <p className={s.errorMessage}>{doc.errorMessage}</p>
                                      {doc.status === 'low_quality' && (
                                        <p className={s.qualityAdvice}>
                                          <strong>Suggestion:</strong> Request driver to re-upload with better lighting, focus, or image quality.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  
                                  <Accordion className={s.nestedAccordion}>
                                    <Card>
                                      <Accordion.Toggle as={Card.Header} eventKey="raw-data" className={s.rawDataToggle}>
                                        View Raw Textract Data
                                      </Accordion.Toggle>
                                      <Accordion.Collapse eventKey="raw-data">
                                        <Card.Body className={s.rawDataBody}>
                                          {this.renderRawTextractData(doc.id)}
                                        </Card.Body>
                                      </Accordion.Collapse>
                                    </Card>
                                  </Accordion>
                                </div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Accordion.Collapse>
                      </Card>
                    ))}
                  </Accordion>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Document Upload Section */}
        <Row className={bt.space3}>
          <Col md={12}>
            <Card>
              <Card.Header>
                <h5>Upload New Document</h5>
              </Card.Header>
              <Card.Body>
                <p>Upload documents on behalf of the driver. Documents will be processed automatically with Textract.</p>
                <Row>
                  {this.getAvailableDocumentTypes().map((docType) => (
                    <Col md={4} key={docType.value} className={bt.space2}>
                      <Button
                        className={s.uploadButton}
                        onClick={() => this.openUploadModal(docType.value)}
                        block
                        style={{
                          backgroundColor: 'var(--btn-secondary-bg)',
                          borderColor: 'var(--common-color)',
                          color: 'var(--btn-secondary-color)',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'var(--btn-primary-bg)';
                          e.target.style.color = 'var(--btn-primary-color)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'var(--btn-secondary-bg)';
                          e.target.style.color = 'var(--btn-secondary-color)';
                        }}
                      >
                        <div>
                          <div>Upload {docType.label}</div>
                          <small style={{ 
                            fontSize: '10px', 
                            fontWeight: '600',
                            color: docType.required ? '#dc3545' : '#6c757d'
                          }}>
                            {docType.required ? 'REQUIRED' : 'OPTIONAL'}
                          </small>
                        </div>
                      </Button>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Upload Modal */}
        <Modal show={this.state.showUploadModal} onHide={this.closeUploadModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              Upload {this.getDocumentTypeLabel(this.state.selectedDocumentType)} 
              {this.isDocumentRequired(this.state.selectedDocumentType) && 
                <span style={{ marginLeft: '10px', fontSize: '14px', color: '#dc3545', fontWeight: '600' }}>(REQUIRED)</span>
              }
              {!this.isDocumentRequired(this.state.selectedDocumentType) && 
                <span style={{ marginLeft: '10px', fontSize: '14px', color: '#6c757d', fontWeight: '600' }}>(OPTIONAL)</span>
              }
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.state.uploading ? (
              <div className={s.uploadingSection}>
                <div className={s.uploadProgress}>
                  <div 
                    className={s.progressBar}
                    style={{ width: `${this.state.uploadProgress}%` }}
                  ></div>
                </div>
                <p>Uploading... {this.state.uploadProgress}%</p>
              </div>
            ) : (
              <Form>
                <Form.Group>
                  <Form.Label>Select Document File</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*,.pdf"
                    onChange={this.handleFileUpload}
                  />
                  <Form.Text className="text-muted">
                    Accepted formats: JPG, PNG, PDF (max 10MB)
                  </Form.Text>
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeUploadModal} disabled={this.state.uploading}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Image Modal removed - documents now open in new tab */}
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withStyles(s, bt),
  graphql(getDriverDocuments, {
    name: 'driverDocuments',
    options: (props) => ({
      variables: {
        driverId: props.driverId
      },
      ssr: false,
      fetchPolicy: 'network-only'
    })
  })
)(DocumentViewer);